import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { VideoTypes } from "@bloomberg/media-ui-av";

import { DEFAULT_VISIBILITY_THRESHOLD } from "~components/VideoPlayer/VideoPlayerContext";
import { useIntersectionObserverTracker } from "~hooks/useIntersectionObserverTracker";

declare type ViewportState = "enter" | "leave";
export default function useVideoViewportAutoplay(
    playerRef: RefObject<
        VideoTypes.MediaPlayer | VideoTypes.PlaylistMediaPlayer
    >,
    isBlocked = false
) {
    const [viewState, setViewState] = useState<ViewportState>();
    const [userStarted, setUserStarted] = useState(false);

    const intersectionObserverTracker = useIntersectionObserverTracker(
        () => setViewState("enter"),
        () => setViewState("leave"),
        DEFAULT_VISIBILITY_THRESHOLD
    );

    useEffect(() => {
        if (!playerRef?.current) return;

        if (isBlocked) {
            setUserStarted(false);
        }

        // isPlaying only exists on instances of MediaPlayer
        // getCurrentPlayer only exists on instances of PlaylistMediaPlayer
        const isPlaying =
            "getCurrentPlayer" in playerRef.current
                ? (playerRef.current as VideoTypes.PlaylistMediaPlayer)
                      .getCurrentPlayer()
                      ?.isPlaying()
                : (playerRef.current as VideoTypes.MediaPlayer).isPlaying();

        if (!userStarted && isPlaying) {
            setUserStarted(true);
        }

        if (viewState === "enter") {
            if (userStarted) {
                playerRef.current.play();
            }

            return;
        }

        if (viewState === "leave") {
            if (!isPlaying) setUserStarted(false);

            playerRef.current.pause();
        }
    }, [playerRef, isBlocked, userStarted, viewState]);

    const onReady = useCallback(() => {
        if (playerRef.current) {
            playerRef.current.on("willPlay", () => setUserStarted(true));
        }
    }, [playerRef]);

    return useMemo(
        () => ({
            onReady,
            intersectionObserverTracker,
        }),
        [intersectionObserverTracker, onReady]
    );
}
